import { Boxes, Globe, TrendingUp, DollarSign, Sparkles, AlertCircle, Briefcase } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useApps } from '@/hooks/useApps'
import { useHostingSites } from '@/hooks/useHostingSites'
import { Card, CardTitle, SectionHeader, Skeleton, Empty, Button } from '@/components/ui'
import { AppCard } from '@/components/AppCard'
import { formatCurrency, cn } from '@/lib/utils'
import { rankApps } from '@/lib/scoring'

export function DashboardPage() {
  const { data: apps, isLoading } = useApps()
  const { data: sites } = useHostingSites()

  if (isLoading) return <DashboardSkeleton />

  if (!apps || apps.length === 0) {
    return (
      <Empty
        icon={<Boxes size={48} />}
        title="Portfolio is empty"
        description="Run the workspace scanner to seed Firestore, or add an app manually."
        action={<Link to="/settings"><Button>Open Settings</Button></Link>}
      />
    )
  }

  const totalValuation = apps.reduce((sum, a) => sum + (a.valuation?.acquisition.midpoint ?? 0), 0)
  const activeCount = apps.filter((a) => a.status === 'active').length
  const wipCount = apps.filter((a) => a.status === 'wip').length
  const customDomainCount = sites?.filter((s) => s.custom_domains.length > 0).length ?? 0

  const hot = rankApps(apps, 'heat').slice(0, 6)
  const valuable = rankApps(apps, 'value').slice(0, 6)

  return (
    <div className="space-y-12">
      <SectionHeader
        eyebrow="Operator Console"
        title="Portfolio Overview"
        description="Heat, valuation, and exit-readiness across every app you've shipped."
        action={
          <Link to="/apps">
            <Button variant="gold-outline">Open Portfolio →</Button>
          </Link>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={<Boxes size={20} />}
          eyebrow="Apps Indexed"
          value={apps.length.toString()}
          sub={`${activeCount} active · ${wipCount} WIP`}
          tone="gold"
        />
        <KPICard
          icon={<DollarSign size={20} />}
          eyebrow="Portfolio Value"
          value={formatCurrency(totalValuation, { compact: true })}
          sub="Acquisition midpoint, all apps"
          tone="success"
        />
        <KPICard
          icon={<Globe size={20} />}
          eyebrow="Hosting Sites"
          value={(sites?.length ?? 0).toString()}
          sub={`${customDomainCount} custom domains`}
          tone="info"
        />
        <KPICard
          icon={<Briefcase size={20} />}
          eyebrow="In Pipeline"
          value={apps.filter((a) => a.acquisition && a.acquisition.stage !== 'not-listed').length.toString()}
          sub="Acquisition opportunities"
          tone="bronze"
        />
      </div>

      <section>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="eyebrow mb-1.5">Hottest</div>
            <h2 className="font-heading text-2xl text-cream">Recent activity, ranked</h2>
          </div>
          <Sparkles size={20} className="text-gold-soft" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {hot.map((app, i) => <AppCard key={app.slug} app={app} index={i} />)}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="eyebrow mb-1.5">Most valuable</div>
            <h2 className="font-heading text-2xl text-cream">Top of acquisition list</h2>
          </div>
          <TrendingUp size={20} className="text-success" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {valuable.map((app, i) => <AppCard key={app.slug} app={app} index={i} />)}
        </div>
      </section>

      <ValuationDisclaimer />
    </div>
  )
}

function KPICard({ icon, eyebrow, value, sub, tone }: {
  icon: React.ReactNode
  eyebrow: string
  value: string
  sub: string
  tone: 'gold' | 'bronze' | 'success' | 'info'
}) {
  const toneCls = {
    gold: 'text-gold-soft border-gold/30',
    bronze: 'text-bronze border-bronze/30',
    success: 'text-success border-success/30',
    info: 'text-info border-info/30',
  }[tone]
  return (
    <div className="glass rounded-lg p-5 border-l-2" style={undefined}>
      <div className={cn('flex items-center gap-2 mb-3', toneCls)}>
        {icon}
        <span className="eyebrow">{eyebrow}</span>
      </div>
      <div className="font-display italic text-4xl text-cream font-light leading-none mb-1">
        {value}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/50 mt-2">
        {sub}
      </div>
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      <Skeleton className="h-20 w-full" />
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
      <div className="grid grid-cols-3 gap-4">
        {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-56" />)}
      </div>
    </div>
  )
}

function ValuationDisclaimer() {
  return (
    <Card className="border-bronze/30 !p-5">
      <div className="flex gap-3">
        <AlertCircle size={18} className="text-bronze shrink-0 mt-0.5" />
        <div>
          <CardTitle className="!text-base mb-1">Valuation methodology — caveat</CardTitle>
          <p className="font-body text-sm text-cream/65 leading-relaxed">
            Valuation bands are <em>directional, not authoritative</em>. They blend signals from
            complexity, lines of code, stack breadth, last-modified recency, and revenue model.
            Real exit pricing is set by buyer interest, comparable transactions, and term sheet
            negotiation. Use these bands to prioritize where to invest sales effort — not as
            asking prices in a real LOI without independent corroboration.
          </p>
        </div>
      </div>
    </Card>
  )
}
